import { useCallback } from 'react'
import { formatUsdValue, useBsvExchangeRate } from 'api/useBsvExchangeRate'
import { BITCOIN_UNIT, SATOSHI_UNIT, SATOSHIS, USD_UNIT, format, toBitcoin } from 'lib/bsv-util'
import { shouldDisplayBitcoin } from 'lib/simpleMoneyToggle'
import useStore from 'state/knovStore'

type SatsDisplayOptions = {
    includeUnit?: boolean
    compactSats?: boolean
    fallbackUnit?: typeof BITCOIN_UNIT | typeof SATOSHI_UNIT
}

const normalizeSats = (sats: number | string | null | undefined) => {
    const value = Number(sats || 0)
    if (!Number.isFinite(value)) return 0
    return value
}

export function formatSatsAsUsd(
    sats: number | string | null | undefined,
    exchangeRate: number | null,
) {
    if (!exchangeRate) return null
    return formatUsdValue((normalizeSats(sats) / SATOSHIS) * exchangeRate)
}

export type SatsDisplayUnit = typeof USD_UNIT | typeof BITCOIN_UNIT | typeof SATOSHI_UNIT

/**
 * The unit `formatSatsForBsvUnit` renders an amount in: dollars when the
 * preference is USD and a rate is known, bitcoin when the amount (or the
 * fallback) calls for it, else satoshis. Surfaces that draw their own unit
 * mark (the sats bolt) ask this instead of parsing the formatted label.
 */
export function satsDisplayUnit(
    sats: number | string | null | undefined,
    bsvUnit: string,
    exchangeRate: number | null,
    { fallbackUnit = SATOSHI_UNIT }: Pick<SatsDisplayOptions, 'fallbackUnit'> = {},
): SatsDisplayUnit {
    const normalizedSats = normalizeSats(sats)
    if (bsvUnit === USD_UNIT && formatSatsAsUsd(normalizedSats, exchangeRate)) return USD_UNIT
    if (shouldDisplayBitcoin(normalizedSats, bsvUnit, fallbackUnit)) return BITCOIN_UNIT
    return SATOSHI_UNIT
}

export function formatSatsForBsvUnit(
    sats: number | string | null | undefined,
    bsvUnit: string,
    exchangeRate: number | null,
    {
        includeUnit = true,
        compactSats = true,
        fallbackUnit = SATOSHI_UNIT,
    }: SatsDisplayOptions = {},
) {
    const normalizedSats = normalizeSats(sats)
    const unit = satsDisplayUnit(normalizedSats, bsvUnit, exchangeRate, { fallbackUnit })

    if (unit === USD_UNIT) return formatSatsAsUsd(normalizedSats, exchangeRate) as string

    if (unit === BITCOIN_UNIT) {
        const bitcoinValue = toBitcoin(normalizedSats)
        return includeUnit ? `${bitcoinValue} BSV` : bitcoinValue
    }

    const satsValue = compactSats
        ? format(Math.round(normalizedSats))
        : Math.round(normalizedSats).toLocaleString()
    return includeUnit ? `${satsValue} sats` : satsValue
}

export default function useBsvDisplay({ enabled = true }: { enabled?: boolean } = {}) {
    const bsvUnit = useStore(state => state.bsvUnit)
    const useUsd = bsvUnit === USD_UNIT
    const exchangeRate = useBsvExchangeRate({ enabled: enabled && useUsd })

    const formatSatsAmount = useCallback(
        (sats: number | string | null | undefined, options?: SatsDisplayOptions) =>
            formatSatsForBsvUnit(sats, bsvUnit, exchangeRate, options),
        [bsvUnit, exchangeRate],
    )

    const formatSatsUsd = useCallback(
        (sats: number | string | null | undefined) => formatSatsAsUsd(sats, exchangeRate),
        [exchangeRate],
    )

    const displayUnit = useCallback(
        (
            sats: number | string | null | undefined,
            options?: Pick<SatsDisplayOptions, 'fallbackUnit'>,
        ) => satsDisplayUnit(sats, bsvUnit, exchangeRate, options),
        [bsvUnit, exchangeRate],
    )

    return {
        bsvUnit,
        exchangeRate,
        useUsd,
        formatSatsAmount,
        formatSatsUsd,
        displayUnit,
    }
}
